import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Center,
  Badge,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import { Image } from "@chakra-ui/react";
import AgGridWrapper from "../../components/AgGrid/AgGridWrapper";
import { GoToPage } from "../../components/CustomPagination/GoToPage";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  FiEye,
  FiGrid,
  FiList,
  FiX,
  FiShoppingBag,
  FiTag,
  FiPackage,
  FiMapPin,
  FiHash,
  FiImage,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle,
  FiDownload,
  FiRotateCw,
  FiZapOff,
} from "react-icons/fi";
import { MdStorefront, MdStore } from "react-icons/md";

import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";
import { InfoCard, ImageCard, ProductTable } from "../storeReport/cards";

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const generatePresignedUrls = async (s3Paths, month, year) => {
  const presignedPromises = s3Paths.map(async (s3Path) => {
    const presignedEndpoint = `${API_CONFIG.SERVICE_URL}${API_CONFIG.ENDPOINTS.S3.PRESIGNED_URL}?fileName=${encodeURIComponent(
      s3Path
    )}&bucketName=${API_CONFIG.S3_BUCKETS.IMAGES}&operation=download&month=${month}&year=${year}&ResponseContentDisposition=attachment`;
    try {
      const response = await fetch(presignedEndpoint);
      if (!response.ok) throw new Error("Failed to fetch presigned URL");
      const presignedUrl = await response.text();
      return presignedUrl.trim();
    } catch (err) {
      console.error("Failed to generate presigned URL:", err);
      return null;
    }
  });
  return (await Promise.all(presignedPromises)).filter((url) => url);
};

const fmt = (val) =>
  val === null || val === undefined ? "—" : String(val);

// ─── Score badge cell ─────────────────────────────────────────────────────────

const ScoreCellRenderer = ({ value }) => (
  <Flex align="center" justify="center" h="100%">
    {value === null || value === undefined ? (
      <Text color="gray.400" fontStyle="italic">—</Text>
    ) : (
      <Badge
        colorScheme="orange"
        fontSize="sm"
        px={3}
        py={1}
        borderRadius="full"
        fontWeight="bold"
      >
        {value}
      </Badge>
    )}
  </Flex>
);

// ─── Component ────────────────────────────────────────────────────────────────

function ActivationDp({ fromDate, toDate }) {
  // ── Table state ────────────────────────────────────────────────────────────
  const [rowData,       setRowData]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);

  // Server-side pagination state (driven by API response)
  const [currentPage,   setCurrentPage]   = useState(0); 
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages,    setTotalPages]    = useState(0);

  // ── Detail modal state ─────────────────────────────────────────────────────
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedRow,          setSelectedRow]          = useState(null);
  const [activeModalTab,       setActiveModalTab]       = useState(0);
  const [loadingDetails,       setLoadingDetails]       = useState(false);

  // Abstract view
  const [storeDetails,         setStoreDetails]         = useState(null);
  const [storeImages,          setStoreImages]          = useState([]);
  const [currentImageIndex,    setCurrentImageIndex]    = useState(0);
  const [imageRotation,        setImageRotation]        = useState(0);

  // Detail view
  const [scoreBreakdown,       setScoreBreakdown]       = useState(null);
  const [productSummary,       setProductSummary]       = useState([]);
  const [shelfInfo,            setShelfInfo]            = useState([]);
  const [actualImageUrl,       setActualImageUrl]       = useState("");
  const [annotatedImageUrl,    setAnnotatedImageUrl]    = useState("");
  const [modalActualImages,    setModalActualImages]    = useState([]);
  const [modalAnnotatedImages, setModalAnnotatedImages] = useState([]);
  const [modalImageIndex,      setModalImageIndex]      = useState(0);
  const [coolerActualImageUrl,       setCoolerActualImageUrl]       = useState("");
  const [coolerAnnotatedImageUrl,    setCoolerAnnotatedImageUrl]    = useState("");
  const [modalCoolerActualImages,    setModalCoolerActualImages]    = useState([]);
  const [modalCoolerAnnotatedImages, setModalCoolerAnnotatedImages] = useState([]);
  const [modalCoolerImageIndex,      setModalCoolerImageIndex]      = useState(0);
  const [activationImageUrl,         setActivationImageUrl]         = useState("");
  const [isActivationImageOpen,      setIsActivationImageOpen]      = useState(false);
  const [loadingActivationImage,     setLoadingActivationImage]     = useState(false);

  // ── Build date body ────────────────────────────────────────────────────────

  const buildDateBody = useCallback(() => {
    const [fromYear, fromMonth, fromDay] = fromDate.split("-").map(Number);
    const [toYear,   toMonth,   toDay]   = toDate.split("-").map(Number);
    return {
      from: { date: fromDay,  month: fromMonth, year: fromYear },
      to:   { date: toDay,    month: toMonth,   year: toYear   },
    };
  }, [fromDate, toDate]);

  // ── Fetch activation summary ───────────────────────────────────────────────

  const fetchData = useCallback(async (page = 0) => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError(null);
    setRowData([]);

    try {
      const res = await apiClient.post(
        `${API_CONFIG.BASE_URL}/api/activation/summary?page=${page}&size=${PAGE_SIZE}`,
        buildDateBody()
      );

      const data = res.data;
      const content       = Array.isArray(data) ? data : (data?.content ?? []);
      const totalEl       = data?.totalElements ?? content.length;
      const totalPg       = data?.totalPages    ?? 1;
      const pageNumber    = data?.number        ?? page;

      setRowData(
        content.map((item) => ({
          storeId:           item?.storeId           ?? "",
          storeName:         item?.storeName         ?? "",
          storeType:         item?.storeType         ?? "",
          activationScore:   item?.activationScore   ?? null,
          fdPrevCount:       item?.fdPrevCount       ?? "—",
          tdPrevCount:       item?.tdPrevCount       ?? "—",
          fdQualifiedCount:  item?.fdQualifiedCount  ?? "—",
          tdQualifiedCount:  item?.tdQualifiedCount  ?? "—",
          fdDetectedCount:   item?.fdDetectedCount   ?? "—",
          tdDetectedCount:   item?.tdDetectedCount   ?? "—",
          _raw:              item,
        }))
      );

      setTotalElements(totalEl);
      setTotalPages(totalPg);
      setCurrentPage(pageNumber);
    } catch (err) {
      console.error("Failed to fetch activation data:", err);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, buildDateBody]);

  // Re-fetch from page 0 whenever dates change
  useEffect(() => {
    fetchData(0);
  }, [fetchData]);

  // ── Pagination handlers ────────────────────────────────────────────────────

  const handlePrevious = () => {
    if (currentPage > 0) fetchData(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) fetchData(currentPage + 1);
  };

  const handleGoToPage = (page) => { // page is 1-based
    fetchData(Math.min(Math.max(page - 1, 0), totalPages - 1));
  };

  // ── Eye-click → open detail modal ─────────────────────────────────────────

  const handleEyeClick = async (rowNode) => {
    setSelectedRow(rowNode);
    setActiveModalTab(0);
    setStoreDetails(null);
    setStoreImages([]);
    setScoreBreakdown(null);
    setProductSummary([]);
    setShelfInfo([]);
    setActualImageUrl("");
    setAnnotatedImageUrl("");
    setModalActualImages([]);
    setModalAnnotatedImages([]);
    setModalCoolerActualImages([]);
    setModalCoolerAnnotatedImages([]);
    setModalImageIndex(0);
    setModalCoolerImageIndex(0);
    setCurrentImageIndex(0);
    setImageRotation(0);
    onOpen();
    setLoadingDetails(true);

    try {
      const body      = buildDateBody();
      const [, toMonth, toYear] = toDate.split("-").map(Number);
      const storeId   = rowNode.storeId;

      // Abstract view: store details + images
      const detailsRes = await apiClient.post(
        `${API_CONFIG.BASE_URL}/red-master/store-details?storeId=${storeId}`,
        body
      );
      const details = detailsRes.data || null;
      setStoreDetails(details);

      if (details?.imagePaths?.length > 0) {
        const urls = await generatePresignedUrls(
          details.imagePaths.map((p) => `${p}`),
          toMonth,
          toYear
        );
        setStoreImages(urls);
      }

      // Detail view: score breakdown
      const sid = details?.storeId || storeId;
      if (sid) {
        const breakdownRes = await apiClient.post(
          `${API_CONFIG.BASE_URL}/red-master/${sid}/score-breakdown`,
          body
        );
        const bd = breakdownRes.data;
        setScoreBreakdown(bd);
        setProductSummary(bd?.availability?.products || []);

        const actualPaths    = bd?.availability?.imagePathResponse?.s3pathActualFile    || [];
        const annotatedPaths = bd?.availability?.imagePathResponse?.s3pathAnnotatedFile || [];
        const coolerAP       = bd?.cooler?.imagePathResponse?.s3pathActualFile          || [];
        const coolerAnnotP   = bd?.cooler?.imagePathResponse?.s3pathAnnotatedFile       || [];

        setShelfInfo(actualPaths.map((_, i) => ({ label: `Shelf ${i + 1}` })));

        if (actualPaths.length) {
          const urls = await generatePresignedUrls(actualPaths, toMonth, toYear);
          setModalActualImages(urls);
          setActualImageUrl(urls[0] || "");
        }
        if (annotatedPaths.length) {
          const urls = await generatePresignedUrls(annotatedPaths, toMonth, toYear);
          setModalAnnotatedImages(urls);
          setAnnotatedImageUrl(urls[0] || "");
        }
        if (coolerAP.length) {
          const urls = await generatePresignedUrls(coolerAP, toMonth, toYear);
          setModalCoolerActualImages(urls);
          setCoolerActualImageUrl(urls[0] || "");
        }
        if (coolerAnnotP.length) {
          const urls = await generatePresignedUrls(coolerAnnotP, toMonth, toYear);
          setModalCoolerAnnotatedImages(urls);
          setCoolerAnnotatedImageUrl(urls[0] || "");
        }
      }
    } catch (err) {
      console.error("Failed to load store details:", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewActivation = async (detail) => {
    try {
      setLoadingActivationImage(true);
      const s3Path = detail?.s3ActualPath;
      if (!s3Path) return;
      const [, toMonth, toYear] = toDate.split("-").map(Number);
      const urls = await generatePresignedUrls([s3Path], toMonth, toYear);
      setActivationImageUrl(urls[0] || "");
      setIsActivationImageOpen(true);
    } catch (err) {
      console.error("Failed to load activation image:", err);
    } finally {
      setLoadingActivationImage(false);
    }
  };

  const handleDownloadImage = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const response = await fetch(imageUrl, { mode: "cors", cache: "no-cache" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = "image.jpg";
      document.body.appendChild(link); link.click();
      document.body.removeChild(link); window.URL.revokeObjectURL(url);
    } catch {
      const link = document.createElement("a");
      link.href = imageUrl; link.download = "image.jpg";
      link.target = "_blank"; link.rel = "noopener noreferrer";
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    }
  };

  const handlePrevModalImage = () => {
    if (modalImageIndex > 0) {
      const i = modalImageIndex - 1;
      setModalImageIndex(i);
      setActualImageUrl(modalActualImages[i] || "");
      setAnnotatedImageUrl(modalAnnotatedImages[i] || "");
    }
  };

  const handleNextModalImage = () => {
    if (modalImageIndex < modalActualImages.length - 1) {
      const i = modalImageIndex + 1;
      setModalImageIndex(i);
      setActualImageUrl(modalActualImages[i] || "");
      setAnnotatedImageUrl(modalAnnotatedImages[i] || "");
    }
  };

  // ── Column definitions ─────────────────────────────────────────────────────

  const EyeCellRenderer = ({ data }) => (
    <Flex align="center" justify="center" h="100%">
      <IconButton
        aria-label="View details"
        icon={<FiEye />}
        size="sm"
        colorScheme="red"
        variant="ghost"
        color="red.500"
        _hover={{ bg: "red.50", transform: "scale(1.15)" }}
        transition="all 0.2s"
        onClick={() => handleEyeClick(data)}
      />
    </Flex>
  );

  const centeredStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    color: "#2D3748",
  };

  const columnDefs = [
    {
      headerName: "VIEW",
      field: "_action",
      minWidth: 80,
      maxWidth: 90,
      flex: 0,
      sortable: false,
      filter: false,
      suppressMovable: true,
      cellRenderer: EyeCellRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    { headerName: "STORE ID",           field: "storeId",          minWidth: 130, flex: 1 },
    { headerName: "STORE NAME",         field: "storeName",        minWidth: 190, flex: 1.6 },
    { headerName: "STORE TYPE",         field: "storeType",        minWidth: 130, flex: 1 },
    {
      headerName: "ACTIVATION SCORE",
      field: "activationScore",
      minWidth: 150,
      flex: 1,
      cellRenderer: ScoreCellRenderer,
      cellStyle: centeredStyle,
    },
    {
      headerName: "FD PREV COUNT",
      field: "fdPrevCount",
      minWidth: 130,
      flex: 1,
      cellStyle: centeredStyle,
      valueFormatter: (p) => fmt(p.value),
    },
    {
      headerName: "TD PREV COUNT",
      field: "tdPrevCount",
      minWidth: 130,
      flex: 1,
      cellStyle: centeredStyle,
      valueFormatter: (p) => fmt(p.value),
    },
    {
      headerName: "FD QUALIFIED",
      field: "fdQualifiedCount",
      minWidth: 130,
      flex: 1,
      cellStyle: centeredStyle,
      valueFormatter: (p) => fmt(p.value),
    },
    {
      headerName: "TD QUALIFIED",
      field: "tdQualifiedCount",
      minWidth: 130,
      flex: 1,
      cellStyle: centeredStyle,
      valueFormatter: (p) => fmt(p.value),
    },
    {
      headerName: "FD DETECTED",
      field: "fdDetectedCount",
      minWidth: 130,
      flex: 1,
      cellStyle: centeredStyle,
      valueFormatter: (p) => fmt(p.value),
    },
    {
      headerName: "TD DETECTED",
      field: "tdDetectedCount",
      minWidth: 130,
      flex: 1,
      cellStyle: centeredStyle,
      valueFormatter: (p) => fmt(p.value),
    },
  ];

  // ── Derived display values for pagination label ────────────────────────────

  const displayPage  = currentPage + 1;           // 1-based for UI
  const displayStart = totalElements === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const displayEnd   = Math.min(displayStart + rowData.length - 1, totalElements);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Record count pill */}
      <Flex align="center" mb={4}>
        <Flex
          align="center" gap={1.5} bg="gray.700" px={4} py={1.5}
          borderRadius="full" boxShadow="0 2px 4px rgba(0,0,0,0.2)"
        >
          <Text fontSize="sm" fontWeight="bold" color="white">Total:</Text>
          <Text color="white" fontSize="md" fontWeight="bold">
            {totalElements} Records
          </Text>
        </Flex>
      </Flex>

      {error && (
        <Text color="red.500" fontWeight="bold" mb={3}>{error}</Text>
      )}

      {/* AG Grid */}
      <Box mb={3}>
        <AgGridWrapper
          rowData={rowData}
          columnDefs={columnDefs}
          loading={loading}
          height="600px"
          extraGridProps={{
            suppressPaginationPanel: true,
            rowHeight: 60,
            headerHeight: 65,
            animateRows: true,
          }}
          noRowsMessage="No records found for the selected date range"
        />
      </Box>

      {/* Server-side Pagination */}
      <Flex justify="space-between" align="center" mt={4} px={2}>
        <Text fontSize="sm" color="gray.600">
          {totalElements > 0
            ? `Showing ${displayStart} to ${displayEnd} of ${totalElements} records`
            : "No records"}
        </Text>
        <Flex gap={2} align="center">
          <Button
            onClick={handlePrevious}
            isDisabled={currentPage === 0 || loading}
            size="sm" variant="outline" colorScheme="gray"
            leftIcon={<ChevronLeftIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Previous
          </Button>
          <Flex
            align="center" justify="center" bg="red.500" color="white"
            w="40px" h="40px" borderRadius="md" fontWeight="bold" fontSize="md"
            boxShadow="0 2px 4px rgba(199,33,39,0.3)"
          >
            {displayPage}
          </Flex>
          <Text fontSize="sm" color="gray.600">
            of {totalPages}
          </Text>
          <GoToPage totalPages={totalPages} onGo={handleGoToPage} isDisabled={loading} />
          <Button
            onClick={handleNext}
            isDisabled={currentPage >= totalPages - 1 || loading}
            size="sm" variant="outline" colorScheme="gray"
            rightIcon={<ChevronRightIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Next
          </Button>
        </Flex>
      </Flex>

      {/* ── Detail Modal ──────────────────────────────────────────────────── */}
      <Modal
        isOpen={isOpen} onClose={onClose} isCentered
        motionPreset="slideInBottom" scrollBehavior="inside"
        blockScrollOnMount={false}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent
          maxW="80vw" w="80vw" maxH="90vh"
          bg="white" borderRadius="2xl" boxShadow="2xl" overflow="hidden"
        >
          {/* Header */}
          <ModalHeader
            bgGradient="linear(to-r, red.600, red.500)"
            color="white" py={6} px={8}
            borderBottom="1px solid" borderColor="red.700"
          >
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={3}>
                <Icon as={MdStorefront} boxSize={7} />
                <Box>
                  <Heading size="md" fontWeight="bold">Store Detail View</Heading>
                  <Text fontSize="sm" color="red.100" mt={1}>
                    {selectedRow?.storeName || "—"} · Activation
                  </Text>
                </Box>
              </Flex>
              <IconButton
                aria-label="Close modal" icon={<FiX />}
                variant="ghost" color="white" _hover={{ bg: "red.700" }}
                onClick={onClose} size="lg"
              />
            </Flex>
          </ModalHeader>

          <ModalBody px={8} py={6} overflowY="scroll">
            {loadingDetails ? (
              <Flex justify="center" align="center" minH="300px">
                <Spinner size="xl" color="red.500" thickness="6px" />
              </Flex>
            ) : (
              <Tabs index={activeModalTab} onChange={(i) => setActiveModalTab(i)}>
                <TabList
                  bg="red.100" p={2} borderRadius="lg" gap={2}
                  display="inline-flex" mb={6}
                >
                  <Tab
                    fontWeight="semibold" fontSize="md" py={3} px={6}
                    borderRadius="lg" color="gray.600"
                    display="flex" gap={2} alignItems="center"
                    _selected={{ bg: "red.600", color: "white", boxShadow: "md" }}
                    _hover={{ bg: "red.200", color: "red.700" }}
                  >
                    <FiGrid size={18} /> Store View
                  </Tab>
                  <Tab
                    fontWeight="semibold" fontSize="md" py={3} px={6}
                    borderRadius="lg" color="gray.600"
                    display="flex" gap={2} alignItems="center"
                    _selected={{ bg: "red.600", color: "white", boxShadow: "md" }}
                    _hover={{ bg: "red.200", color: "red.700" }}
                  >
                    <FiList size={18} /> Detail View
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* ── Abstract View ── */}
                  <TabPanel p={0}>
                    {storeDetails ? (
                      <VStack spacing={6} align="stretch">
                        <Flex direction={{ base: "column", md: "row" }} gap={4} align="stretch">
                          {/* Store Info */}
                          <Box
                            p={4} bg="white" borderRadius="xl"
                            border="1px solid" borderColor="gray.200" boxShadow="md" flex="1"
                          >
                            <Heading size="sm" mb={4} color="gray.800" fontWeight="bold">
                              Store Information
                            </Heading>
                            <VStack align="start" spacing={3}>
                              {[
                                { icon: FiShoppingBag, color: "red.600",    label: "Store Name",  value: storeDetails?.storeName  },
                                { icon: FiTag,         color: "green.600",  label: "Store Type",  value: storeDetails?.storeType  },
                                { icon: FiPackage,     color: "purple.600", label: "Store Class", value: storeDetails?.storeClass },
                                { icon: FiHash,        color: "yellow.600", label: "Store ID",    value: storeDetails?.storeId    },
                              ].map(({ icon, color, label, value }) => (
                                <HStack key={label} align="start" spacing={3}>
                                  <Icon as={icon} color={color} fontSize="20px" />
                                  <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="medium">{label}</Text>
                                    <Text fontSize="sm" color="gray.800" fontWeight="semibold">{value || "—"}</Text>
                                  </Box>
                                </HStack>
                              ))}
                              {storeDetails?.address && (
                                <HStack align="start" spacing={3}>
                                  <Icon as={FiMapPin} color="orange.600" fontSize="20px" />
                                  <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="medium">Address</Text>
                                    <Text fontSize="sm" color="gray.800" fontWeight="semibold">{storeDetails.address}</Text>
                                  </Box>
                                </HStack>
                              )}
                            </VStack>
                          </Box>

                          {/* Store Scores */}
                          <Box
                            p={4} bg="white" borderRadius="xl"
                            border="1px solid" borderColor="gray.200" boxShadow="md" flex="1"
                          >
                            <Heading size="sm" mb={4} color="gray.800" fontWeight="bold">
                              Store Scores
                            </Heading>
                            <SimpleGrid columns={1} spacing={3}>
                              {[
                                { label: "Availability",    value: storeDetails?.availabilityScore, bg: "green.50",  border: "green.200",  text: "green.700",  val: "green.600"  },
                                { label: "Activation",      value: storeDetails?.activationScore,   bg: "orange.50", border: "orange.200", text: "orange.700", val: "orange.600" },
                                { label: "Cooler",          value: storeDetails?.coolerScore,       bg: "red.50",    border: "red.200",    text: "red.700",    val: "red.600"    },
                                { label: "Cooler Purity",   value: storeDetails?.coolerPurity,      bg: "blue.50",   border: "blue.200",   text: "blue.600",   val: "blue.600"   },
                                { label: "Cooler Impurity", value: storeDetails?.coolerImpurity,    bg: "gray.50",   border: "gray.200",   text: "gray.600",   val: "gray.600"   },
                                { label: "Cooler Bonus",    value: storeDetails?.coolerBonus,       bg: "purple.50", border: "purple.200", text: "purple.700", val: "purple.600" },
                              ].map(({ label, value, bg, border, text, val }) => (
                                <Box
                                  key={label} textAlign="center" p={3} bg={bg}
                                  borderRadius="lg" border="1px solid" borderColor={border}
                                >
                                  <Text fontSize="sm" color={text} fontWeight="medium">{label}</Text>
                                  <Text fontSize="lg" fontWeight="bold" color={val}>{value ?? "—"}</Text>
                                </Box>
                              ))}
                            </SimpleGrid>
                          </Box>
                        </Flex>

                        {/* Store Images */}
                        <Box>
                          <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={4}>
                            Store Images
                          </Text>
                          {storeImages.length > 0 ? (
                            <VStack spacing={4}>
                              <Box
                                position="relative" w="full" maxW="480px" mx="auto"
                                borderRadius="xl" overflow="hidden" boxShadow="xl" bg="gray.100"
                              >
                                <Image
                                  src={storeImages[currentImageIndex]}
                                  alt={`Store image ${currentImageIndex + 1}`}
                                  objectFit="contain" w="100%" h="300px" bg="white"
                                  transform={`rotate(${imageRotation}deg)`}
                                  transition="transform 0.3s ease"
                                />
                                <IconButton
                                  aria-label="Rotate image" icon={<Icon as={FiRotateCw} />}
                                  position="absolute" top="2" right="10"
                                  colorScheme="blue" size="sm" zIndex={2}
                                  onClick={() => setImageRotation((p) => (p + 90) % 360)}
                                />
                                <IconButton
                                  aria-label="Download image" icon={<FiDownload />}
                                  position="absolute" top="2" right="2"
                                  colorScheme="red" size="sm" zIndex={2}
                                  onClick={() => handleDownloadImage(storeImages[currentImageIndex])}
                                />
                                {storeImages.length > 1 && (
                                  <>
                                    <Button
                                      position="absolute" left="4" top="50%" transform="translateY(-50%)"
                                      onClick={() => setCurrentImageIndex((i) => Math.max(0, i - 1))}
                                      isDisabled={currentImageIndex === 0}
                                      size="sm" bg="whiteAlpha.900" boxShadow="md" zIndex={1}
                                    >
                                      <Icon as={FiChevronLeft} boxSize={5} />
                                    </Button>
                                    <Button
                                      position="absolute" right="4" top="50%" transform="translateY(-50%)"
                                      onClick={() => setCurrentImageIndex((i) => Math.min(storeImages.length - 1, i + 1))}
                                      isDisabled={currentImageIndex === storeImages.length - 1}
                                      size="sm" bg="whiteAlpha.900" boxShadow="md" zIndex={1}
                                    >
                                      <Icon as={FiChevronRight} boxSize={5} />
                                    </Button>
                                  </>
                                )}
                              </Box>
                              {storeImages.length > 1 && (
                                <Text fontSize="sm" color="gray.600" fontWeight="medium">
                                  {currentImageIndex + 1} / {storeImages.length}
                                </Text>
                              )}
                              {storeImages.length > 1 && (
                                <HStack spacing={2} overflowX="auto" py={2} justify="center">
                                  {storeImages.map((img, idx) => (
                                    <Box
                                      key={idx} onClick={() => setCurrentImageIndex(idx)}
                                      cursor="pointer" opacity={idx === currentImageIndex ? 1 : 0.5}
                                      border={idx === currentImageIndex ? "2px solid" : "none"}
                                      borderColor="red.500" borderRadius="md" overflow="hidden"
                                      transition="all 0.2s" _hover={{ opacity: 0.8 }}
                                    >
                                      <Image src={img} alt={`thumb ${idx}`} w="60px" h="60px" objectFit="cover" />
                                    </Box>
                                  ))}
                                </HStack>
                              )}
                            </VStack>
                          ) : (
                            <Center h="200px" bg="gray.50" borderRadius="xl" border="2px dashed" borderColor="gray.300">
                              <Text color="gray.500" fontWeight="medium">No store images available</Text>
                            </Center>
                          )}
                        </Box>
                      </VStack>
                    ) : (
                      <Center h="300px">
                        <Text color="gray.500">No store details available</Text>
                      </Center>
                    )}
                  </TabPanel>

                  {/* ── Detail View ── */}
                  <TabPanel p={0}>
                    {scoreBreakdown ? (
                      <VStack spacing={6} align="stretch">
                        {/* Store Overview */}
                        <Box
                          bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
                          boxShadow="lg" p={6} position="relative" overflow="hidden"
                          _before={{
                            content: '""', position: "absolute", top: 0, left: 0,
                            width: "6px", height: "100%", bg: "red.600",
                          }}
                        >
                          <Flex align="center" mb={4}>
                            <Icon as={MdStore} color="red.600" boxSize={6} mr={3} />
                            <Heading size="md" color="gray.800" fontWeight="semibold">Store Overview</Heading>
                          </Flex>
                          {(() => {
                            const store = scoreBreakdown.store || {};
                            return (
                              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                                <InfoCard icon={FiHash}    label="Store ID"             value={store.storeId      || "—"} color="red"    />
                                <InfoCard icon={FiTag}     label="Store Code"            value={store.storeId      || "—"} color="green"  />
                                <InfoCard icon={MdStore}   label="Store Name"            value={store.storeName    || "—"} color="purple" />
                                <InfoCard icon={FiPackage} label="Store Type - Class"    value={`${store.storeType || "—"} - ${store.storeClass || "—"}`} color="orange" />
                                <InfoCard icon={FiPackage} label="Cooler Size"           value={store.coolerSize   || "—"} color="blue"   />
                                <InfoCard icon={FiMapPin}  label="Address"               value={store.storeAddress || "—"} color="red" colSpan={{ base: 1, md: 2, lg: 3 }} />
                              </SimpleGrid>
                            );
                          })()}
                        </Box>

                        {/* Activation Score Summary */}
                        <Box
                          bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
                          boxShadow="lg" p={6}
                        >
                          <Flex align="center" justify="space-between" mb={4}>
                            <Flex align="center">
                              <Icon as={FiZapOff} color="orange.500" boxSize={6} mr={3} />
                              <Heading size="md" color="gray.800" fontWeight="semibold">Activation Score</Heading>
                            </Flex>
                            <Badge colorScheme="orange" fontSize="md" variant="solid" px={4} py={2} borderRadius="full">
                              {scoreBreakdown?.activation?.activationScore != null
                                ? `${scoreBreakdown.activation.activationScore} out of 10`
                                : "N/A"}
                            </Badge>
                          </Flex>
                          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                            {[
                              { label: "FD Prev Count",      value: selectedRow?.fdPrevCount      },
                              { label: "TD Prev Count",      value: selectedRow?.tdPrevCount      },
                              { label: "FD Qualified Count", value: selectedRow?.fdQualifiedCount },
                              { label: "TD Qualified Count", value: selectedRow?.tdQualifiedCount },
                              { label: "FD Detected Count",  value: selectedRow?.fdDetectedCount  },
                              { label: "TD Detected Count",  value: selectedRow?.tdDetectedCount  },
                            ].map(({ label, value }) => (
                              <Box
                                key={label} p={3} bg="orange.50" borderRadius="lg"
                                border="1px solid" borderColor="orange.200" textAlign="center"
                              >
                                <Text fontSize="xs" color="orange.700" fontWeight="medium">{label}</Text>
                                <Text fontSize="lg" fontWeight="bold" color="orange.600">
                                  {value ?? "—"}
                                </Text>
                              </Box>
                            ))}
                          </SimpleGrid>
                        </Box>

                        {/* Product Summary */}
                        <Box
                          bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
                          boxShadow="lg" p={6}
                        >
                          <Flex align="center" justify="space-between" mb={4}>
                            <Flex align="center">
                              <Icon as={FiGrid} color="red.600" boxSize={6} mr={3} />
                              <Heading size="md" color="gray.800" fontWeight="semibold">Product Summary</Heading>
                            </Flex>
                            <HStack spacing={3}>
                              <Badge colorScheme="green" variant="subtle" px={3} py={1} fontSize="md">
                                availabilityScore:{" "}
                                {scoreBreakdown?.availability?.availabilityScore != null
                                  ? `${scoreBreakdown.availability.availabilityScore} out of 70`
                                  : "0 out of 70"}
                              </Badge>
                              <Badge colorScheme="red" variant="subtle" px={3} py={1} borderRadius="full">
                                {productSummary.length} items
                              </Badge>
                            </HStack>
                          </Flex>
                          {productSummary.length > 0 ? (
                            <Box borderRadius="lg" overflow="hidden" border="1px solid" borderColor="gray.200">
                              <ProductTable productSummary={productSummary} />
                            </Box>
                          ) : (
                            <Center h="200px">
                              <Text color="gray.500">No products found</Text>
                            </Center>
                          )}
                        </Box>

                        {/* Visual Evidence */}
                        <Box
                          bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
                          boxShadow="lg" p={6}
                        >
                          <Flex align="center" justify="space-between" mb={4}>
                            <Flex align="center">
                              <Icon as={FiImage} color="red.600" boxSize={6} mr={3} />
                              <Heading size="md" color="gray.800" fontWeight="semibold">Visual Evidence</Heading>
                            </Flex>
                            {shelfInfo[modalImageIndex] && (
                              <Badge colorScheme="red" fontSize="md" px={4} py={2} borderRadius="full" fontWeight="bold">
                                {shelfInfo[modalImageIndex].label}
                              </Badge>
                            )}
                          </Flex>
                          <HStack spacing={6} mb={4} minH="300px">
                            <ImageCard
                              title="Actual Image"
                              subtitle="Original store photograph"
                              icon={FiEye}
                              imageUrl={actualImageUrl}
                              colorScheme="red"
                            />
                            <ImageCard
                              title="Annotated Image"
                              subtitle="AI-processed analysis"
                              icon={FiCheckCircle}
                              imageUrl={annotatedImageUrl}
                              colorScheme="green"
                            />
                          </HStack>
                          <HStack justify="center" mt={4} spacing={4}>
                            <IconButton
                              aria-label="Previous image" icon={<FiChevronLeft />}
                              onClick={handlePrevModalImage} isDisabled={modalImageIndex <= 0}
                              colorScheme="red" variant="outline" size="md"
                            />
                            <Text fontSize="md" color="gray.700" fontWeight="semibold" minW="80px" textAlign="center">
                              {modalActualImages.length > 0
                                ? `${modalImageIndex + 1} / ${modalActualImages.length}`
                                : "0 / 0"}
                            </Text>
                            <IconButton
                              aria-label="Next image" icon={<FiChevronRight />}
                              onClick={handleNextModalImage}
                              isDisabled={modalImageIndex >= Math.max(0, modalActualImages.length - 1)}
                              colorScheme="red" variant="outline" size="md"
                            />
                          </HStack>
                        </Box>

                        {/* Activation Details */}
                        <Box
                          bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200"
                          boxShadow="lg" p={6}
                        >
                          <Flex align="center" justify="space-between" mb={4}>
                            <Flex align="center">
                              <Icon as={FiCheckCircle} color="orange.500" boxSize={6} mr={3} />
                              <Heading size="md" color="gray.800" fontWeight="semibold">Activation Details</Heading>
                            </Flex>
                            <Badge colorScheme="orange" fontSize="md" variant="solid" px={4} py={2}>
                              activationScore:{" "}
                              {scoreBreakdown?.activation?.activationScore != null
                                ? `${scoreBreakdown.activation.activationScore} out of 10`
                                : "0 out of 10"}
                            </Badge>
                          </Flex>
                          {scoreBreakdown?.activation?.activationDetails?.length > 0 ? (
                            <TableContainer>
                              <Table variant="simple">
                                <Thead bg="orange.50">
                                  <Tr>
                                    <Th color="orange.700" fontWeight="bold">Company</Th>
                                    <Th color="orange.700" fontWeight="bold">Brand</Th>
                                    <Th color="orange.700" fontWeight="bold">Product</Th>
                                    <Th color="orange.700" fontWeight="bold">Action</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {scoreBreakdown.activation.activationDetails.map((detail, idx) => (
                                    <Tr key={idx} bg={idx % 2 === 0 ? "orange.50" : "transparent"}>
                                      <Td fontWeight="medium">
                                        {detail.product?.productCompany?.productCompanyName || "—"}
                                      </Td>
                                      <Td>{detail.product?.productBrand?.productBrandName || "—"}</Td>
                                      <Td>{detail.product?.productName || "—"}</Td>
                                      <Td>
                                        <Button
                                          size="sm" colorScheme="orange"
                                          onClick={() => handleViewActivation(detail)}
                                        >
                                          View
                                        </Button>
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Center h="200px" color="gray.500">
                              No activation details available
                            </Center>
                          )}
                        </Box>
                      </VStack>
                    ) : (
                      <Center h="300px">
                        <VStack spacing={4}>
                          <Icon as={MdStore} boxSize={16} color="gray.400" />
                          <Text fontSize="lg" color="gray.600" fontWeight="semibold">
                            No Detail Data Available
                          </Text>
                        </VStack>
                      </Center>
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>

          <ModalFooter px={8} py={4} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
            <Flex w="100%" justify="space-between" align="center">
              <Text fontSize="sm" color="gray.600">&copy; 2025 Image Insight. All rights reserved.</Text>
              <Button
                variant="outline" colorScheme="gray" leftIcon={<FiX />}
                onClick={onClose} size="lg" px={8} borderRadius="lg"
              >
                Close
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Activation Image Modal */}
      <Modal
        isOpen={isActivationImageOpen}
        onClose={() => setIsActivationImageOpen(false)}
        isCentered size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Activation Image</ModalHeader>
          <ModalBody>
            {loadingActivationImage ? (
              <Center py={8}><Spinner /></Center>
            ) : activationImageUrl ? (
              <Box position="relative">
                <Image src={activationImageUrl} alt="Activation" width="100%" borderRadius="md" />
                <IconButton
                  aria-label="Download activation image" icon={<FiDownload />}
                  position="absolute" top="2" right="2" colorScheme="red" size="sm"
                  onClick={() => handleDownloadImage(activationImageUrl)}
                />
              </Box>
            ) : (
              <Center py={8} color="gray.600">No image available</Center>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsActivationImageOpen(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default ActivationDp;